/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ChatProps,
  useDataChannel,
  useLocalParticipant,
  ReceivedChatMessage,
  ChatEntry,
  formatChatMessageLinks,
} from '@livekit/components-react'
import { ToolTip } from 'base/tooltip/tooltip'
import { cloneSingleChild } from 'livekit/LiveKit.utils'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomRoomContext } from '../CustomRoomContext'

interface ChatMessages {
  id: string
  timestamp: number
  message: string
  from: any
  system?: boolean
}

export function LiveShoppingChat({
  messageFormatter,
  messageDecoder,
  messageEncoder,
  className,
  isPipEnabled,
  ...props
}: ChatProps & { isPipEnabled?: boolean }) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const ulRef = React.useRef<HTMLUListElement>(null)

  const { message: chatmsg, send, isSending } = useDataChannel('livekitChat')

  const [messageLengthExceed, setMessageLengthExceed] = React.useState(false)
  const [chatMessages, setChatMessages] = React.useState<ChatMessages[]>([])
  const { localParticipant } = useLocalParticipant()

  const { t } = useTranslation()
  const { joinedParticipants, setJoinedParticipants } = useCustomRoomContext()

  const [selectedUser, setSelectedUser] = React.useState<{ name: string; id?: string } | null>(null)

  const capitalizeFirstLetter = (text: string) => {
    if (!text) return ''
    const linkRegex = /(https?:\/\/[^\s]+)/g
    const messageParts = text.split(linkRegex)

    return messageParts
      .map((part) => (part.match(linkRegex) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
      .join('')
  }

  React.useEffect(() => {
    if (joinedParticipants.length > 0) {
      setChatMessages((prev) => [
        ...prev,
        ...joinedParticipants.map((name) => ({
          id: Math.random().toString(),
          timestamp: Date.now(),
          message: `${name} joined`,
          from: { name: 'System' },
          system: true,
        })),
      ])
      setJoinedParticipants([])
    }
  }, [joinedParticipants])

  React.useEffect(() => {
    if (chatmsg) {
      const chatMessage = JSON.parse(new TextDecoder().decode(chatmsg.payload)) as ReceivedChatMessage

      if (chatmsg.from?.sid === localParticipant?.sid) return

      const newMessage: ChatMessages = {
        id: chatMessage.id,
        timestamp: chatMessage.timestamp,
        message: capitalizeFirstLetter(chatMessage.message),
        from: chatmsg.from,
      }

      setChatMessages((prev) => [...prev, newMessage])
    }
  }, [chatmsg, localParticipant?.sid])

  const scrollToBottom = () => {
    if (ulRef && ulRef.current) {
      ulRef.current.scrollTo({ top: ulRef.current.scrollHeight })
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (inputRef.current && ulRef.current) {
      const message = inputRef.current.value.trim()
      if (message !== '') {
        if (message.length > 500) {
          setMessageLengthExceed(true)
          return
        }
        setMessageLengthExceed(false)

        const encodedMessage = new TextEncoder().encode(
          JSON.stringify({ id: localParticipant?.sid, message, timestamp: Date.now() })
        )

        const sentMessage: ChatMessages = {
          id: localParticipant?.sid,
          timestamp: Date.now(),
          message: capitalizeFirstLetter(message),
          from: { name: 'You' },
        }

        send(encodedMessage, { reliable: true })
        setChatMessages((prev) => [...prev, sentMessage])
        inputRef.current.value = ''
        inputRef.current.focus()
      }
    }
  }

  function handleMessageChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value.length <= 500) {
      setMessageLengthExceed(false)
    }
  }

  React.useEffect(() => {
    scrollToBottom()
  }, [chatMessages])

  React.useEffect(() => {
    const systemMessages = chatMessages.filter((msg) => msg.system)
    if (systemMessages.length > 0) {
      const timer = setTimeout(() => {
        setChatMessages((prev) => prev.filter((msg) => !msg.system))
      }, 5000)
      return () => clearTimeout(timer)
    }
    return () => {}
  }, [chatMessages])

  const UserModal = ({ username, onClose }: { username: string; onClose: () => void }) => {
    const modalRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
          onClose()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [onClose])

    return (
      <div ref={modalRef} className="bg-white text-black w-80 rounded-lg shadow-lg border border-gray-400 p-4 relative">
        {/* Username & Follow */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-gray-700 w-10 h-10 flex items-center justify-center rounded-full text-lg font-bold">
              {username.slice(0, 2).toUpperCase()}
            </div>
            <div className="text-sm">{username}</div>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2">
          <button
            onClick={() => console.log('View Profile')}
            className="flex items-center justify-between p-3 w-full text-sm rounded-lg hover:bg-gray-200"
          >
            <span className="flex items-center">👤 View Profile</span>
            <span>{'>'}</span>
          </button>
          <button
            onClick={() => console.log('Message clicked')}
            className="flex justify-between items-center p-3 w-full text-sm hover:bg-gray-200"
          >
            <span className="flex items-center gap-2">💬 Message</span>
            <span>{'>'}</span>
          </button>
          <button
            onClick={() => console.log('Block')}
            className="flex items-center justify-between p-3 w-full text-sm rounded-lg hover:bg-gray-200"
          >
            <span className="flex items-center">🚫 Block</span>
            <span>{'>'}</span>
          </button>
          {/* <hr className="border-gray-300" /> */}
          <button
            onClick={() => console.log('Report')}
            className="flex items-center justify-between p-3 w-full text-sm rounded-lg hover:bg-gray-200"
          >
            <span className="flex items-center">⚠️ Report</span>
            <span>{'>'}</span>
          </button>
        </div>
      </div>
    )
  }
  return (
    <>
      <div
        {...props}
        className={`flex flex-col w-full h-full bg-white border-gray-200 border-solid border-2 rounded-lg`}
      >
        {/* Modal */}
        {selectedUser && (
          <div className="absolute bottom-28 z-50">
            <UserModal username={selectedUser.name} onClose={() => setSelectedUser(null)} />
          </div>
        )}
        {/* Top */}
        <div className="flex justify-between bg-amber-500 p-1 rounded-t-md">
          <div>
            <span id="chatHeader" className="text-white">
              {t('liveChat')}
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="overflow-y-auto py-2 flex flex-1 flex-col gap-2 w-full bg-white">
          <ul ref={ulRef}>
            {props.children
              ? chatMessages.map((msg, idx) =>
                  cloneSingleChild(props.children, {
                    entry: msg,
                    key: idx,
                    messageFormatter,
                  })
                )
              : (() => {
                  const seenParticipants = new Set<string>()

                  return chatMessages.map((msg, idx, allMsg) => {
                    if (msg.system) {
                      if (msg.message.includes('joined') && seenParticipants.has(msg.message)) {
                        return null
                      }
                      seenParticipants.add(msg.message)

                      return (
                        <li key={idx} className="text-gray-500 text-sm text-center">
                          {capitalizeFirstLetter(msg.message)}
                        </li>
                      )
                    }

                    let slicedName = msg.from?.name || ''
                    if (slicedName.length > 30) {
                      slicedName = slicedName.slice(0, 30) + '...'
                    }

                    const previousMsg = allMsg[idx - 1]
                    const previousId = previousMsg?.from?.id || ''

                    const isSameUserAsPrevious = msg.from?.id === previousId
                    const hideName = idx >= 1 && isSameUserAsPrevious

                    const slicedMsg = {
                      ...msg,
                      from: { ...msg.from, name: slicedName },
                    } as ChatMessages

                    return (
                      <li key={idx} className="px-4">
                        <div className="flex gap-2">
                          <div
                            className="bg-gray-700 w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold cursor-pointer"
                            onClick={() => setSelectedUser(msg.from)}
                          >
                            {slicedMsg.from?.name?.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            {!hideName && (
                              <div className="flex items-center justify-between mb-1">
                                <div
                                  className="text-sm text-blue-500 hover:underline cursor-pointer"
                                  onClick={() => setSelectedUser(msg.from)}
                                >
                                  {slicedMsg.from?.name}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {new Date(msg.timestamp).toLocaleTimeString(navigator.language, {
                                    timeStyle: 'short',
                                  })}
                                </span>
                              </div>
                            )}
                            <ChatEntry
                              hideName={true}
                              hideTimestamp={true}
                              entry={slicedMsg}
                              messageFormatter={formatChatMessageLinks}
                            />
                          </div>
                        </div>
                      </li>
                    )
                  })
                })()}
          </ul>
        </div>

        {/* Send */}
        <div className="flex">
          <form className="w-full" onSubmit={handleSubmit}>
            <div className="flex flex-row w-full p-1">
              <input
                disabled={isSending}
                ref={inputRef}
                className="bg-transparent relative flex flex-1 w-full rounded-l-md rounded-r-none py-2 pl-4 pr-10 text-gray-400 placeholder:text-sm placeholder-gray-400 text-base focus:outline-none focus:ring-1 focus:ring-yellow-400 focus:border-transparent border border-gray-300 h-full"
                type="text"
                id="input_typeMsg"
                placeholder={t('typeAMessage')}
                onChange={handleMessageChange}
              />
              <div className="flex flex-col justify-center rounded-r-md rounded-l-none border-r border-t border-b border-gray-300">
                <button disabled={isSending} type="submit" className="p-1">
                  <ToolTip tip={t('send')}>
                    <FontAwesomeIcon id="btn_sendMessage" icon={faPaperPlane} className="text-gray-500" />
                  </ToolTip>
                </button>
              </div>
            </div>
            {messageLengthExceed && (
              <div id="msgLengthError" className="text-red-500 p-0.5 text-sm">
                {t('messageLengthExceeds')} 500 {t('characters')}
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  )
}
